/**
 * A list of events that may be broadcast on $rootScope.
 */
const EventNames = Object.freeze({
    // Fired when a request to the DigiTickets API returns a 401 response.
    API_REQUEST_UNAUTHORIZED: 'apiRequestUnauthorized',

    // @see src/js/lib/Input/KeyboardInputDetector.js
    BARCODE_SCANNED: 'barcodeScanned',

    // Called when a payment is added to or removed from the cart.
    CART_PAYMENTS_CHANGED: 'cartPaymentsChanged',

    // Called when a payment is removed from the cart.
    CART_PAYMENT_REMOVED: 'cartPaymentRemoved',

    // A customer has been added to an order after order creation.
    CUSTOMER_ADDED_TO_ORDER: 'customerAddedToOrder',

    // Device has been set either by the user selecting it, or the stored device was loaded from localstorage.
    DEVICE_SET: 'deviceSet',

    // The EposDataSyncer has been run.
    // This does not necessarily mean that it has downloaded fresh data, just that
    // the data in IndexedDB is correct for the current branch.
    EPOS_DATA_SYNCED: 'eposDataSynced',

    // Broadcast by the Vue GiftVoucherPaymentMethod component when a gift voucher is selected and the user
    // clicks "Use Voucher".
    GIFT_VOUCHER_SELECTED_FOR_USE: 'giftVoucherSelectedForUse',

    // Item branch changed (either from branch A to branch B or from no branch to branch A).
    ITEM_BRANCH_CHANGED: 'itemBranchChanged',

    // The ItemSyncer has been run.
    // This does not necessarily mean that it has downloaded fresh data, just that
    // the items in IndexedDB are correct for the current item branch.
    ITEMS_SYNCED: 'itemsSynced',

    KEY_DOWN: 'keyDown',
    ESC_KEY_DOWN: 'escKeyDown',

    KITCHEN_DATA_SYNCED: 'kitchenDataSynced',

    // Something was added to or removed from the online queue.
    ONLINE_QUEUE_MODIFIED: 'onlineQueueModified',

    // Device went from online to offline or vice-versa.
    ONLINE_STATUS_CHANGED: 'onlineStatusChanged',

    // Branch changed (either from branch A to branch B or from no branch to branch A).
    ORDER_BRANCH_CHANGED: 'orderBranchChanged',

    // An order that was previously unconfirmed has been confirmed.
    ORDER_CONFIRMED: 'orderConfirmed',

    // An unconfirmed order has been voided.
    ORDER_VOIDED: 'orderVoided',

    // The selected payment method has changed.
    PAYMENT_METHOD_CHANGED: 'paymentMethodChanged',

    PAYYO_WEB_SOCKET_CONNECTED: 'payyoWebSocketConnected',
    PAYYO_WEB_SOCKET_ERROR: 'payyoWebSocketError',

    // A document finished printed (either successfully or unsuccessfully).
    PRINTING_FINISHED: 'printingFinished',

    // The PrivilegesSyncer has been run.
    // This does not necessarily mean that it has downloaded fresh data, just that
    // the privileges in IndexedDB have been checked and exist.
    PRIVILEGES_SYNCED: 'privilegesSynced',

    // @see src/js/lib/Input/KeyboardInputDetector.js
    RFID_TAG_SCANNED: 'rfidTagScanned',

    SET_VOID_OR_REFUND_IN_PROGRESS: 'setVoidOrRefundInProgress',

    // A ticket / instance has been (un)redeemed.
    // This will not necessarily file for every single redemption / unredemption, but will be fired if
    // something changes about the redemption state of one or more items in the order.
    TICKET_REDEMPTION_ACTIVITY: 'ticketRedemptionActivity',

    // Trading session has been created.
    TRADING_SESSION_CREATED: 'tradingSessionCreated',

    TRADING_SESSION_EVENT_CREATED: 'tradingSessionEventCreated',

    // User has logged in.
    // This may be a new login, or the existing logged in user may have been loaded from localstorage.
    USER_LOGGED_IN: 'userLoggedIn',

    /**
     * The following events are emitted by Vue to trigger something to happen in Angular...
     */
    ADD_ITEM_TO_CART_REQUESTED: 'addItemToCartRequested',
    ADD_PAYMENT_TO_CART_REQUESTED: 'addPaymentToCartRequested',
    INSTANCE_REDEMPTION_BOX_REDEEM_ITEM_INSTANCE: 'instanceRedemptionBoxRedeemItemInstance',
    INSTANCE_REDEMPTION_BOX_UNREDEEM_ITEM_INSTANCE_CHECK: 'instanceRedemptionBoxUnredeemItemInstanceCheck',
    LINE_REDEMPTION_BOX_REDEEM_ALL_REMAINING_ON_LINE: 'lineRedemptionBoxRedeemAllRemainingOnLine',
    LINE_REDEMPTION_BOX_REDEEM_ONE: 'lineRedemptionBoxRedeemOne',
    LINE_REDEMPTION_BOX_UNREDEEM_ALL_ON_LINE_CHECK: 'lineRedemptionBoxUnredeemAllOnLineCheck',
    LINE_REDEMPTION_BOX_UNREDEEM_ONE: 'lineRedemptionBoxUnredeemOne',
    MEMBERSHIP_REDEMPTION_ACTIVITY: 'membershipRedemptionActivity',
    MEMBERSHIP_REDEMPTION_BUTTON_TOGGLE_TILE_MEMBERSHIP: 'membershipRedemptionButtonToggleTileMembership',
    ORDER_DETAILS_MEMBERSHIP_CLEAR_RENEWING_AND_VIEW_MEMBERSHIP: 'orderDetailsMembershipClearRenewingAndViewMembership',
    ORDER_DETAILS_MEMBERSHIP_EDIT_MEMBERSHIP: 'orderDetailsMembershipEditMembership',
    ORDER_DETAILS_MEMBERSHIP_LOG_VISIT: 'orderDetailsMembershipLogVisit',
    ORDER_DETAILS_MEMBERSHIP_TOGGLE_MEMBER_VISIT: 'orderDetailsMembershipToggleMemberVisit',
    ORDER_DETAILS_MEMBERSHIP_UNLOG_VISIT: 'orderDetailsMembershipUnlogVisit',
    ORDER_DETAILS_TAB_CHANGED: 'orderDetailsTabChanged',
    ORDER_ITEMS_TABLE_LINE_CLICK: 'orderItemsTableLineClick',
    PAY_ORDER_BALANCE_REQUESTED: 'payOrderBalanceRequested',
    PAY_REQUESTED: 'payRequested', // Tell the SellCtrl to "click" the pay button.
    REDEEM_GROUP_BUTTON_REDEEM_GROUP: 'redeemGroupButtonRedeemGroup',
    REDEEM_GROUP_BUTTON_UNREDEEM_GROUP: 'redeemGroupButtonUnredeemGroup',

    // Broadcast by the Vue RedeemOrderButton component (in the Order Summary card) when the user clicks
    // "Redeem Order". Payload is an array of OrderItemGroup IDs (only those with something left to redeem),
    // not the whole tab or itemType, so targeting stays explicit rather than relying on "current" state.
    REDEEM_ORDER_BUTTON_REDEEM_ORDER: 'redeemOrderButtonRedeemOrder',

    START_EVENT_BOOKING_REQUESTED: 'startEventBookingRequested',
});

module.exports = EventNames;
